import { EMPTY_TEXT_FILTERED } from '@/constants/messages';

/**
 * Sentences the customer's own common spec words differently from RAH.
 *
 * `src/constants/messages.ts` stays as it is: those constants are read by ~20
 * medix screens across three portals, and KEiLOG's spec is not theirs to adopt.
 * A KEiLOG screen that needs the customer's wording imports it from here; one
 * whose sentence RAH and the customer agree on keeps importing the shared
 * constant, so only the genuine differences live in this file.
 *
 * Each constant carries its row number in the table
 * (`[HITOWA_KEiLOG]_Common_共通仕様_v1.2.xlsx`, Validation message) so the next
 * reader can check it without guessing where it came from.
 */

/**
 * No.25 — RAH says CSVを出力しました。, the customer's table adds ファイル.
 *
 * 📌 Its two neighbours, CSVの出力に失敗しました。 and PDFの生成に失敗しました。,
 * are not in the table yet (BA to add). Until they are there is no KEiLOG
 * wording to differ from, so those keep coming from `@/constants/messages`.
 */
export const CSV_SUCCESS_MESSAGE = 'CSVファイルを出力しました。';

/**
 * Common spec, Validation message No.2 (Selection required).
 *
 * SCR-084 names it for both bulk buttons — 一括印刷 and CSV出力 (BD 5.9 / 5.10):
 * neither does anything until a 利用者 is ticked.
 */
export const SELECTION_REQUIRED_MESSAGE = '利用者を指定してください。';

/**
 * Common spec, Validation message No.17 (No existing data).
 *
 * SCR-084 前回からコピー uses it when the 利用者 has no earlier 施術報告書: the
 * button stays enabled (BD 7.1), so pressing it has to answer something.
 *
 * SCR-014 reuses it for the BD's 「Bản ghi đã bị người khác xoá」 case: the
 * insurer endpoints answer 404 once the row is gone.
 */
export const NO_EXISTING_DATA_MESSAGE = '既存のデータがありません。';

/**
 * Common 共通仕様 v1.3 「データテーブル表示」 — the empty table on FIRST LOAD.
 *
 * 📌 Not the same sentence as `@/constants/messages`'s `EMPTY_TEXT_FILTERED`
 * (「該当する検索結果がありません。」). The customer's table separates the two:
 * this one when a list screen opens with no rows at all, that one only after a
 * search returned nothing.
 */
export const EMPTY_TEXT_INITIAL = '該当するデータはありません。';

/**
 * Which of the two empty lines a list shows — the ONE rule, for every tab that has both.
 *
 * `searched` means somebody pressed 🔍 検索. BA named exactly that discriminator for SCR-050's
 * 同意書 (Consent form) tab in question B31 on 2026-10-05: the automatic first read counts as
 * 初期表示 (Initial display), so it answers No.30, and only a press of 検索 moves to No.31.
 *
 * It lives here because the three tabs of 利用者管理 (User management) used to disagree: 同意書 asked
 * 「has anyone searched」 while 利用者 and 保険証 (Insurance card) asked 「is anything in scope at
 * all」, so one screen answered two different ways depending on the tab. The reviewer unified them on
 * 検索 on 2026-10-06. A rule copied into three files drifts; a rule called from three files cannot.
 */
export const emptyListText = (searched: boolean): string =>
  searched ? EMPTY_TEXT_FILTERED : EMPTY_TEXT_INITIAL;

/**
 * Common 共通仕様 v1.2 §4 — HTTP 403.
 *
 * RAH has no constant for it: `getApiErrorMessage` only ever reads the body, so
 * a 403 with no `message` used to surface as 「データの取得に失敗しました。」.
 * SCR-014 items 10 and 22 both name this sentence, so it lives here and
 * `utils/apiStatusMessage` maps the status onto it.
 */
export const FORBIDDEN_MESSAGE = 'この操作を行う権限がありません。';

/**
 * Common 共通仕様 v1.2 §4 — HTTP 5xx.
 *
 * 📌 Deliberately NOT the same sentence as `@/constants/messages`'s
 * `SERVER_ERROR_MESSAGE` (「エラーが発生しました。しばらくしてからもう一度お試しください。」):
 * the customer's table words it shorter, and SCR-014 item 10 quotes this form.
 */
export const SERVER_ERROR_MESSAGE = 'サーバーエラーが発生しました。';

/**
 * Common 共通仕様 v1.2 — optimistic-lock conflict.
 *
 * The same sentence 施術報告書 already uses (`pages/treatment-report/utils/revision.ts`);
 * it is shared here so a second screen does not import across features.
 *
 * SCR-014 item 22 shows it on 編集 when another user has changed the row: the
 * KEiLOG insurer update compares `updated_at` and answers 409 without writing.
 */
export const RECORD_UPDATED_BY_OTHER_MESSAGE =
  '他のユーザーによって更新されています。最新の内容を確認してください。';

/**
 * SCR-084 BD 4.1 — 拠点全体 is 「chỉ search và view」.
 *
 * Both panes disable their buttons under that 表示範囲, so the sentence lives
 * here rather than on either of them: a message one component owns and the
 * other imports ties the two together for no reason, and a test that doubles
 * the owner loses the constant with it.
 *
 * It names the way back, because the operator has not lost a right — they are
 * on the other of the two lists (common spec 22-2.0: a disabled control says
 * why).
 */
export const SCOPE_VIEW_ONLY_MESSAGE =
  '拠点全体では閲覧のみです。編集するには「自分の担当」に切り替えてください。';

/**
 * Common spec `L4.2` — 「サーバーエラーが発生しました。」
 *
 * RAH's own `SERVER_ERROR_MESSAGE` says 「エラーが発生しました。しばらくしてから
 * もう一度お試しください。」 and is shown as a modal by `GlobalBehaviorWrapper`.
 * The customer's table names both a different sentence and a different way of
 * showing it (toast), so a KEiLOG screen opts its queries out of the shared
 * modal (`meta.suppressServerErrorModal`) and says this instead.
 */
export const SERVER_ERROR_TOAST_MESSAGE = 'サーバーエラーが発生しました。';

/**
 * Common spec `L4.5` — 「タイムアウトが発生しました。再度実行してください。」
 *
 * The same clause forbids calling the endpoint again by itself, so a screen
 * showing this must not retry: it tells the 施術家 to, and waits.
 */
export const TIMEOUT_TOAST_MESSAGE = 'タイムアウトが発生しました。再度実行してください。';

/**
 * Common spec `L4.4` — 「この操作を行う権限がありません。」
 *
 * For a 403 on a DATA call, where the screen stays put and the session is kept.
 * The 403 PAGE is a different sentence and belongs to `ForbiddenPage`.
 */
export const ACTION_FORBIDDEN_TOAST_MESSAGE = 'この操作を行う権限がありません。';

/**
 * Common spec `V44` — 「このページを表示する権限がありません。」, with 「ホームに戻る」.
 *
 * The 403 PAGE, not the 403 toast above: the screen was refused before it was
 * drawn, so there is nothing to stay on and the caller is offered the way home.
 * `ForbiddenPage` still defaults to the portal's older wording; a screen that
 * has to meet `V44` passes this through `RequirePermission`.
 */
export const PAGE_FORBIDDEN_MESSAGE = 'このページを表示する権限がありません。';

/**
 * One block of a screen could not be read — shown inside that block, with
 * 「再読み込み」 beside it, while the blocks that did answer stay drawn
 * (SCR-045-009 BD 12.5).
 */
export const BLOCK_LOAD_FAILED_MESSAGE = '処理に失敗しました。';

/** The button that asks one failed block again. */
export const BLOCK_RELOAD_LABEL = '再読み込み';

/**
 * Common spec `L3.5` — added beside the loading state once a request has been
 * running for 30 seconds, so a slow answer does not read as a dead screen.
 */
export const STILL_WORKING_MESSAGE = '処理中です。しばらくお待ちください。';

/** How long a request runs before {@link STILL_WORKING_MESSAGE} is added (`L3.5`). */
export const STILL_WORKING_AFTER_MS = 30_000;

/** Common spec `V9` — a date the field cannot read, said inline under / beside that field. */
export const DATE_FORMAT_INVALID_MESSAGE = '日付の形式が不正です。';

/** Common spec `V13` (`L10.2`) — a typed date range whose start is after its end, said inline. */
export const DATE_RANGE_ORDER_MESSAGE = '開始日は終了日より前の日付を指定してください。';

/** Common spec `V1` — 「{item name}を記入してください。」, inline at a required field left blank. */
export const requiredFieldMessage = (itemName: string): string => `${itemName}を記入してください。`;

/**
 * Common spec `2.0` — 「{item name}を指定してください。」, inline at a required field whose
 * value is picked rather than typed (dropdown, radio, checkbox, date picker).
 *
 * The named form of {@link SELECTION_REQUIRED_MESSAGE}, for fields other than 利用者.
 */
export const selectionRequiredFieldMessage = (itemName: string): string =>
  `${itemName}を指定してください。`;

/** Common spec `V23` — the toast once a print view has been handed over. */
export const PRINT_DONE_MESSAGE = '印刷が完了しました。';

/** A request that never reached the server (offline / connection refused). */
export const NETWORK_ERROR_MESSAGE = 'インターネット接続エラーが発生しました。';

/**
 * Common spec v1.3, Validation message No.26 — the toast after a QR was read and
 * matched to a 利用者 (SCR-146-001 利用者QRスキャン).
 */
export const QR_READ_SUCCESS_MESSAGE = 'QR読み込みが完了しました。';

/**
 * Common spec v1.3, Validation message No.32 — 探す pressed with the search box
 * empty. A warning toast, as `design-guideline` shows it.
 */
export const SEARCH_KEY_REQUIRED_MESSAGE = '検索キーを指定してください。';

/** Common spec v1.3, Validation message No.20 — a new record (or 下書き) was stored. */
export const REGISTERED_MESSAGE = '登録しました。';

/**
 * Common spec v1.3, Validation message No.33 — the generic processing failure.
 * Same sentence as `BLOCK_LOAD_FAILED_MESSAGE`; named for what a toast after an
 * action means, so a screen does not read as "a block failed to load".
 */
export const PROCESS_FAILED_MESSAGE = '処理に失敗しました。';

/**
 * Common spec, Validation message No.38 (`BD/common/COMMON-MESSAGES.md` `38.0`) — a request
 * answered 401: the session ended and the app returns to the login screen. The API's own 401
 * body says RAH's 「トークンが切れました。…」 (`ApiExceptionHandler`); `apiStatusMessage`
 * shows this sentence instead.
 */
export const SESSION_EXPIRED_MESSAGE =
  'セッションの有効期限が切れました。再度ログインしてください。';

/**
 * Common spec v1.2 — an external service (KEiLOG, the geocoding service) did not
 * answer or answered with an error. The backend returns it with 502; the screen
 * shows it as a toast without inventing its own wording.
 */
export const EXTERNAL_SERVICE_ERROR_MESSAGE =
  '外部サービスでエラーが発生しました。しばらく時間をおいて再度お試しください。';

/**
 * SCR-005 ログイン, button 「ログイン」 【B】 — a sign-in that failed with no sentence of its
 * own to show (connection lost, timeout). Shown under 「パスワード」.
 */
export const LOGIN_FAILED_MESSAGE = 'ログインに失敗しました';

/** Common spec, Validation message No.19 — the toast of a save that went through. */
export const UPDATED_MESSAGE = '更新しました。';
